import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ButtonsHeader from "@/app/components/common/ButtonsHeader";
import {
  defaultPaginationOptions,
  endpoints,
  ProductStatus,
} from "muminsmart-utils";
import ProductListing from "@/app/components/product/ProductListing";
import ProductListingSkeleton from "@/app/components/product/ProductListingSkeleton";
import { useGetSellerQuery } from "@/lib/api/apiSlice";
import { useShowInitialLoader } from "@/lib/hooks";

export default function SellerPage() {
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const sellerId = params.id ?? searchParams.get("id") ?? undefined;

  const {
    data: seller,
    isLoading,
    isError,
  } = useGetSellerQuery(sellerId!, {
    skip: !sellerId,
  });
  const showLoader = useShowInitialLoader(isLoading, seller);

  const fetchConfig = useMemo(
    () => ({
      endpoint: endpoints.productVariant.paginated,
      query: {
        ...defaultPaginationOptions,
        sortBy: "createdAt",
        sortOrder: "DESC",
        sellerId,
        relations: "product,variantAttributes",
        isActive: true,
        status: ProductStatus.Verified,
      },
      mode: "variant" as const,
      errorMessage: "Error fetching products",
    }),
    [sellerId],
  );

  if (!sellerId) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <p className="text-lg">Seller ID is required</p>
      </div>
    );
  }

  if (showLoader) {
    return (
      <div className="min-h-screen">
        <ButtonsHeader heading="Seller" />
        <div className="mt-4 min-h-[calc(100vh-5rem)] rounded-t-3xl bg-white py-6">
          <div className="px-4">
            <ProductListingSkeleton
              showSearch
              className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <p className="text-lg">Failed to fetch seller</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <ButtonsHeader heading={seller?.name ?? "Seller"} />
      <div className="py-6 mt-4 rounded-t-3xl bg-white min-h-[calc(100vh-5rem)]">
        <div className="px-4">
          <ProductListing
            fetchConfig={fetchConfig}
            className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
            showSearch={true}
          />
        </div>
      </div>
    </div>
  );
}
